import { motion } from "framer-motion";
import { Check } from "lucide-react";

const t = {
  es: {
    label: "Sobre Nosotros",
    title: "Un espacio donde te sientes",
    titleAccent: "como en casa",
    intro:
      "Emprendimiento familiar enfocado en brindar espacios de recreación, cómodos y acogedores, para cada integrante de la familia.",
    desc: "Berlisa del Pacífico nace con la intención de proveer un espacio acogedor donde sus clientes se puedan sentir como en casa. Contamos con un complejo común de 7 Bungalows y uno privado con 6 habitaciones.",
    bungalowTitle: "Los Bungalows incluyen:",
    bungalowFeatures: [
      "Capacidad de 6 personas máximo",
      "1 habitación matrimonial + 1 con cama imperial y litera",
      "Cocina equipada",
      "Sala con TV y A/C en cada ambiente",
      "Jacuzzi privado",
      "Área de churrasquera privada",
      "Piscina y parqueo",
    ],
    complexTitle: "Complejo Privado incluye:",
    complexFeatures: [
      "6 habitaciones con baño privado",
      "4 hab. con 2 camas matrimoniales + 2 hab. cama king",
      "Cocina equipada y equipo de sonido",
      "A/C en cada habitación",
      "Área de churrasquera privada",
      "Piscina y parqueo privado",
    ],
    commonTitle: "Área común del complejo:",
    commonFeatures: [
      "Piscina con sillas para tomar el sol",
      "Palmeras y áreas verdes",
      "Rancho para descansar",
      "Acceso a la playa",
    ],
  },
  en: {
    label: "About Us",
    title: "A space where you feel",
    titleAccent: "right at home",
    intro:
      "A family business focused on providing comfortable and welcoming recreational spaces for every member of the family.",
    desc: "Berlisa del Pacífico was created to provide a cozy space where guests can feel at home. We have a common complex of 7 Bungalows and a private complex with 6 rooms.",
    bungalowTitle: "Bungalows include:",
    bungalowFeatures: [
      "Capacity for 6 people",
      "1 master bedroom + 1 with imperial bed and bunk",
      "Fully equipped kitchen",
      "Living room with TV and A/C in every room",
      "Private jacuzzi",
      "Private BBQ area",
      "Pool and parking",
    ],
    complexTitle: "Private Complex includes:",
    complexFeatures: [
      "6 rooms with private bathrooms",
      "4 rooms with 2 queen beds + 2 king rooms",
      "Kitchen and sound system",
      "A/C in every room",
      "Private BBQ area",
      "Pool and private parking",
    ],
    commonTitle: "Common complex area:",
    commonFeatures: [
      "Pool with sun loungers",
      "Palm trees and green areas",
      "Relaxation palapa",
      "Beach access",
    ],
  },
};

export default function About({ lang }) {
  const tr = t[lang];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-montserrat text-[#8C5E35] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            {tr.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl md:text-5xl leading-tight mb-6"
            data-testid="about-title"
          >
            {tr.title}{" "}
            <span className="text-[#8C5E35]">{tr.titleAccent}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-dm-sans text-gray-600 text-lg leading-relaxed"
          >
            {tr.intro}
          </motion.p>
        </div>

        {/* Main Grid: Video + Bungalow features */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          {/* Video */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl overflow-hidden shadow-2xl shadow-[#1F3A5E]/10 aspect-video"
            data-testid="about-video"
          >
            <iframe
              src="https://www.youtube.com/embed/pBIml7a_oEc"
              title="Berlisa del Pacífico"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </motion.div>

          {/* Bungalow + Complex features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="space-y-8"
          >
            <p className="font-dm-sans text-gray-600 text-base leading-relaxed">
              {tr.desc}
            </p>
            {/* Bungalow features */}
            <div>
              <h3 className="font-montserrat font-bold text-[#1F3A5E] text-lg mb-4">
                {tr.bungalowTitle}
              </h3>
              <ul className="space-y-2">
                {tr.bungalowFeatures.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 font-dm-sans text-gray-600 text-sm">
                    <Check size={16} className="text-[#8C5E35] mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Complex + Common Area */}
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#FAFAF9] rounded-3xl p-8 border border-[#D4B895]/40"
          >
            <h3 className="font-montserrat font-bold text-[#1F3A5E] text-lg mb-5">
              {tr.complexTitle}
            </h3>
            <ul className="space-y-2.5">
              {tr.complexFeatures.map((item, i) => (
                <li key={i} className="flex items-start gap-3 font-dm-sans text-gray-600 text-sm">
                  <Check size={15} className="text-[#8C5E35] mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl overflow-hidden relative"
          >
            <img
              src="/images/about-video.jpg"
              alt="Berlisa del Pacífico"
              loading="lazy"
              className="w-full h-full object-cover min-h-[280px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A5E]/70 to-transparent p-8 flex flex-col justify-end">
              <h3 className="font-montserrat font-bold text-white text-lg mb-4">
                {tr.commonTitle}
              </h3>
              <ul className="space-y-1.5">
                {tr.commonFeatures.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/90 text-sm font-dm-sans">
                    <Check size={13} className="text-[#D4B895]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
